import React from 'react';

import { setLanguage } from '../../utils/i18-next';
import {
  changeLanguage,
  getLanguageFromCookie,
  getLanguageItemFromCookie,
  hasLanguageCookie,
  isValidLanguage,
  languageDropdownListItems,
  SupportedLanguage,
} from '../../utils/text/translation';

// We need to wait some time after the element is in DOM, probably to wait for Google Translate
// to be initialized. Have not found a callback for that in new google.translate.TranslateElement
// (in index.html).
const GOOGLE_TRANSLATE_INIT_DELAY = 500;

export const useLanguage = () => {
  const [selectedLanguage, setSelectedLanguage] = React.useState(getLanguageItemFromCookie());

  // Sync the selector to ?lang= only when the user has no saved cookie preference.
  // If a cookie exists, that preference wins and the selector already reflects it.
  React.useEffect(() => {
    const urlLang = new URLSearchParams(window.location.search).get('lang');
    if (!urlLang || hasLanguageCookie()) {
      return;
    }
    const target = isValidLanguage(urlLang) ? urlLang : 'en';
    const item = languageDropdownListItems.find((x) => x.value === target);
    if (item) {
      setSelectedLanguage(item);
    }
  }, []);

  // Helper function to handle setting and updating language state
  const updateLanguage = React.useCallback(async (languageValue: SupportedLanguage) => {
    const language = languageDropdownListItems.find((x) => x.value === languageValue);
    if (!language) {
      return;
    }

    setSelectedLanguage(language);
    changeLanguage(language.value);

    try {
      await setLanguage(language.value);
    } catch (error) {
      console.error('Failed to set language:', error);
    }
  }, []);

  // Function to select a language based on user input
  const selectLanguage = React.useCallback(
    async (value: SupportedLanguage) => {
      await updateLanguage(value);
    },
    [updateLanguage],
  );

  // Function to select the default language on page load.
  const selectDefaultLanguage = React.useCallback(() => {
    // ?lang= takes priority — don't let the saved cookie override a URL-requested language.
    if (new URLSearchParams(window.location.search).get('lang') !== null) {
      return;
    }

    const savedLanguage = getLanguageFromCookie();

    // Because the default language is Swedish,
    // we do not change the default language.
    if (!isValidLanguage(savedLanguage) || savedLanguage === 'sv') {
      return;
    }

    setTimeout(async () => {
      await updateLanguage(savedLanguage);
    }, GOOGLE_TRANSLATE_INIT_DELAY);
  }, [updateLanguage]);

  return {
    languageItems: languageDropdownListItems,
    selectLanguage,
    selectDefaultLanguage,
    selectedLanguage,
  };
};
